import { useCallback, useState } from 'react';
import { analyzeInsuranceCard, analyzeDualSideCard, MindeeApiError } from '../services/mindee/client';

function formatError(err) {
  if (err instanceof MindeeApiError) {
    if (err.statusCode === 401) return `Invalid API key. (HTTP 401: ${err.body})`;
    if (err.statusCode === 429) return 'Too many requests. Please try again later. (HTTP 429)';
    if (err.statusCode === 400) return `Could not read card image. Please retake the photo. (HTTP 400: ${err.body})`;
    return `API error (HTTP ${err.statusCode}): ${err.body}`;
  }
  if (err instanceof TypeError && err.message.includes('fetch')) return 'No internet connection.';
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return `Unknown error: ${String(err)}`;
}

export function useOcr(apiEndpoint) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const analyze = useCallback(
    async (imageBlob) => {
      setLoading(true);
      setResult(null);
      setError(null);

      try {
        const data = await analyzeInsuranceCard(imageBlob, apiEndpoint);
        setResult(data);
        setLoading(false);
        return data;
      } catch (err) {
        setError(formatError(err));
        setLoading(false);
        throw err;
      }
    },
    [apiEndpoint]
  );

  const analyzeDual = useCallback(
    async (frontBlob, backBlob) => {
      setLoading(true);
      setResult(null);
      setError(null);

      try {
        const data = await analyzeDualSideCard(frontBlob, backBlob, apiEndpoint);
        setResult(data.front);
        setLoading(false);
        return data;
      } catch (err) {
        setError(formatError(err));
        setLoading(false);
        throw err;
      }
    },
    [apiEndpoint]
  );

  const reset = useCallback(() => {
    setLoading(false);
    setResult(null);
    setError(null);
  }, []);

  return { loading, result, error, analyze, analyzeDual, reset };
}
